import { create } from 'zustand'

import { placeholderLabelValue } from '@/constants/common'
import { USE_COMPETITIONS } from '@/constants/licenses'
import { LabelValue } from '@/types/common'

import type { UseLicenseStore } from './useLicensesStore.types'

export const sortCompetitionsOrSeasons = (competitionsOrSeasons: LabelValue[]) => {
  return [...competitionsOrSeasons].sort((a, b) => a.value.localeCompare(b.value))
}

export const useLicenseStore = create<UseLicenseStore>((set, get) => ({
  licenseId: undefined,
  licensePeriods: [],
  selectedCompetitionsOrSeasons: [],
  fetchedCompetitionSeasons: [],
  totalCompetitionSeasonsPages: 0,
  areAllSelected: false,
  licenseProperty: placeholderLabelValue,
  searchCompetitionsQuery: '',
  partiallySelectedCompetitions: [],
  useAllCompetitions: USE_COMPETITIONS.ALL,
  setLicenseId: (licenseId: number) => {
    set({ licenseId })
  },
  setSearchCompetitionsQuery: (searchCompetitionsQuery: string) => {
    set({
      searchCompetitionsQuery
    })
  },
  setLicensePeriods: (licensePeriods) => {
    set({
      licensePeriods
    })
  },
  updateStartDate: (periodIndex, startDate) => {
    const licensePeriods = get().licensePeriods
    licensePeriods[periodIndex].startDate = startDate
    set({
      licensePeriods: [...licensePeriods]
    })
  },
  updateEndDate: (periodIndex, endDate) => {
    const licensePeriods = get().licensePeriods
    licensePeriods[periodIndex].endDate = endDate
    set({
      licensePeriods: [...licensePeriods]
    })
  },
  selectCompetition: (competitionSeason) => {
    const selectedCompetitionsOrSeasons = get().selectedCompetitionsOrSeasons
    const partiallySelectedCompetitions = get().partiallySelectedCompetitions

    const isSelected = selectedCompetitionsOrSeasons.find(
      (compOrSeason) => compOrSeason.value === competitionSeason.value
    )
    const isPartial = partiallySelectedCompetitions.find(
      (compOrSeason) => compOrSeason.value === competitionSeason.value
    )
    if (isSelected && !isPartial) return

    const updatedCompetitionsOrSeasons = !isSelected
      ? [...selectedCompetitionsOrSeasons, competitionSeason]
      : selectedCompetitionsOrSeasons

    // filter from partially selected competitions
    const filteredPartiallySelected = partiallySelectedCompetitions.filter(
      (comp) => comp.value !== competitionSeason.value
    )

    // check if all competitions are selected
    const allCompetitions = get().fetchedCompetitionSeasons.reduce((acc: LabelValue[], curr) => {
      if (acc.findIndex((comp) => comp.value === curr.value) === -1) {
        acc.push(curr)
      }
      return acc
    }, [])
    const areAllSelected = allCompetitions.every(
      (comp) =>
        updatedCompetitionsOrSeasons.findIndex(
          (selectedComp) => comp.value === selectedComp.value
        ) > -1
    )
    set({
      selectedCompetitionsOrSeasons: sortCompetitionsOrSeasons(updatedCompetitionsOrSeasons),
      partiallySelectedCompetitions: sortCompetitionsOrSeasons(filteredPartiallySelected),
      areAllSelected
    })
  },
  setPartiallySelectedCompetitions: (competitionsSeasons = []) => {
    set({
      partiallySelectedCompetitions: sortCompetitionsOrSeasons(competitionsSeasons)
    })
  },
  selectCompetitions: (competitionsSeasons = []) => {
    const allCompetitions = get().fetchedCompetitionSeasons.reduce((acc: LabelValue[], curr) => {
      if (acc.findIndex((comp) => comp.value === curr.value) === -1) {
        acc.push(curr)
      }
      return acc
    }, [])
    const areAllSelected =
      allCompetitions.length > 0 &&
      allCompetitions.every(
        (comp) =>
          competitionsSeasons.findIndex((selectedComp) => comp.value === selectedComp.value) > -1
      )
    set({
      selectedCompetitionsOrSeasons: sortCompetitionsOrSeasons(competitionsSeasons),
      areAllSelected
    })
  },
  removeCompetition: (competitionSeason) => {
    set({
      selectedCompetitionsOrSeasons: get().selectedCompetitionsOrSeasons.filter(
        (comp) => comp.value !== competitionSeason.value
      ),
      areAllSelected: false
    })
  },
  removeAllSelectedCompetitions: () => {
    set({
      selectedCompetitionsOrSeasons: [],
      partiallySelectedCompetitions: [],
      areAllSelected: false
    })
  },
  setFetchedCompetitionSeasons: (fetchedCompetitionSeasons) => {
    set({
      fetchedCompetitionSeasons
    })
  },
  addFetchedCompetitionSeasons: (newData) => {
    set((state) => ({
      ...state,
      fetchedCompetitionSeasons: state.fetchedCompetitionSeasons.concat(newData)
    }))
  },
  resetFetchedCompetitionSeasons: () => {
    set({
      fetchedCompetitionSeasons: []
    })
  },
  setTotalCompetitionSeasonsPages: (count: number) => {
    set({
      totalCompetitionSeasonsPages: count
    })
  },
  toggleSelectAllCompetitions: () => {
    const { fetchedCompetitionSeasons, searchCompetitionsQuery } = get()
    const allCompetitions = fetchedCompetitionSeasons.reduce((acc: LabelValue[], curr) => {
      if (
        acc.findIndex((comp) => comp.value === curr.value) === -1 &&
        (!searchCompetitionsQuery ||
          curr.label.toLowerCase().includes(searchCompetitionsQuery.toLowerCase()))
      ) {
        acc.push(curr)
      }
      return acc
    }, [])

    const areAllSelected = allCompetitions.every(
      (comp) =>
        get().selectedCompetitionsOrSeasons.findIndex(
          (selectedComp) => comp.value === selectedComp.value
        ) > -1
    )
    set({
      selectedCompetitionsOrSeasons: areAllSelected ? [] : allCompetitions,
      areAllSelected: !areAllSelected
    })
  },
  setLicenseProperty: (licenseProperty) => {
    set({
      licenseProperty
    })
  },
  operator: '',
  setOperator: (operator: string) => {
    set({
      operator
    })
  },
  resetStore: () => {
    set({
      licensePeriods: [],
      selectedCompetitionsOrSeasons: [],
      fetchedCompetitionSeasons: [],
      areAllSelected: false,
      licenseProperty: placeholderLabelValue
    })
  },
  newlyAddedPeriods: [],
  setNewlyAddedPeriods: (newlyAddedPeriods) => {
    set({
      newlyAddedPeriods
    })
  },
  resetYears: () => {
    set({
      licensePeriods: []
    })
  },
  setUseAllCompetitions: (useAllCompetitions) => {
    set({
      useAllCompetitions
    })
  }
}))
